import {
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { GuidUtil } from '@liberty-financial/ng-core';

import { DropdownItem } from './dropdown.model';

@Directive({
  selector: 'dropdown-toggle'
})
export class SparkPlusDropdownToggle {}

@Component({
  selector: 'sparkplus-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class SparkPlusDropdownComponent<T> implements OnChanges {
  id = GuidUtil.generate();

  @Input() isOpen = false;
  @Input() items: DropdownItem<T>[] = [];
  @Input() selectedItem: string | null = null;
  @Output() onClose = new EventEmitter<boolean>();
  @Output() onSelection = new EventEmitter<T>();

  @ViewChild('sparkDropDown', { static: false, read: ElementRef })
  dropDown!: ElementRef;

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
    this.close();
  }

  @HostListener('window:scroll', ['$event']) onWindowScroll() {
    if (this.isOpen) {
      this.adjustDropdownHeight();
    }
  }

  @HostListener('window:resize') onWindowResize() {
    if (this.isOpen) {
      this.adjustDropdownHeight();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen']) {
      if (
        changes['isOpen'].previousValue === true &&
        changes['isOpen'].currentValue === false
      ) {
        this.resetDropdownPosition();
      }
    }

    if (this.isOpen) {
      this.adjustDropdownHeight();
    }
  }

  isFloatRight(): boolean {
    const position = this.getPosition(this.dropDown?.nativeElement);
    const containerWidth =
      position.offsetParent?.offsetWidth || window.innerWidth;
    return position.left + 250 >= containerWidth;
  }

  isFloatUp(): boolean {
    // Always open downward - never float up
    return false;
  }

  onClick(item: DropdownItem<T>): void {
    if (this.selectedItem === item.id) {
      return; // guard
    }

    this.onSelection.emit(item.value);
    this.selectedItem = item.id;
    this.close();
  }

  isSelected(item: DropdownItem<T>): boolean {
    return item.id === this.selectedItem;
  }

  close(): void {
    const wasOpen = this.isOpen;
    this.isOpen = false;

    // Only emit onClose if the dropdown was actually open
    // This prevents infinite loops when parent toggles isOpen
    if (wasOpen) {
      this.onClose.emit(true);
    }

    // Clean up any fixed positioning when closing
    this.resetDropdownPosition();
  }

  private resetDropdownPosition(): void {
    if (!this.dropDown?.nativeElement) return;

    const dropdownMenu =
      this.dropDown.nativeElement.querySelector('.dropdown__menu');
    if (dropdownMenu) {
      dropdownMenu.classList.remove('dropdown-modal-fixed');
      dropdownMenu.style.top = '';
      dropdownMenu.style.left = '';
      dropdownMenu.style.width = '';
      dropdownMenu.style.minWidth = '';
      dropdownMenu.style.maxWidth = '';
    }
  }

  private adjustDropdownHeight(): void {
    if (!this.dropDown?.nativeElement) return;

    const dropdownElement = this.dropDown.nativeElement;
    const dropdownMenu = dropdownElement.querySelector('.dropdown__menu');

    if (!dropdownMenu) return;

    const modal = dropdownElement.closest('.modal');

    if (modal) {
      // In modals, ALL dropdowns need fixed positioning to escape modal overflow constraints
      const sparkDropdown = this.dropDown.nativeElement;

      // Find the appropriate toggle element
      const customToggle = sparkDropdown.querySelector(
        '.dropdown-toggle-custom'
      );
      const iconButton = sparkDropdown.querySelector('lds-icon-button');
      const toggle = customToggle || iconButton || sparkDropdown;

      const toggleRect = toggle.getBoundingClientRect();

      // Apply modal-specific styling and positioning
      dropdownMenu.classList.add('dropdown-modal-fixed');
      dropdownMenu.style.setProperty(
        'top',
        `${toggleRect.bottom - 1}px`,
        'important'
      );
      dropdownMenu.style.setProperty(
        'left',
        `${toggleRect.left}px`,
        'important'
      );

      // Apply width matching based on dropdown type
      if (customToggle && !iconButton) {
        // User-dropdown: match exact toggle width
        const exactWidth = toggleRect.width;
        dropdownMenu.style.setProperty('width', `${exactWidth}px`, 'important');
        dropdownMenu.style.setProperty(
          'min-width',
          `${exactWidth}px`,
          'important'
        );
        dropdownMenu.style.setProperty(
          'max-width',
          `${exactWidth}px`,
          'important'
        );
      } else {
        // Icon button dropdown: use natural width with minimum
        dropdownMenu.style.setProperty('min-width', '120px', 'important');
        dropdownMenu.style.removeProperty('width');
        dropdownMenu.style.removeProperty('max-width');
      }

      return;
    } else {
      this.adjustForRegularContainer(dropdownMenu);
    }
  }

  private adjustForRegularContainer(dropdownMenu: any): void {
    const sparkDropdown = this.dropDown.nativeElement;
    const customToggle = sparkDropdown.querySelector('.dropdown-toggle-custom');
    const iconButton = sparkDropdown.querySelector('lds-icon-button');

    // Only apply adjustments to user-dropdown components, not basic icon dropdowns
    if (customToggle && !iconButton) {
      // This is a user-dropdown component
      const availableHeight = this.calculateAvailableHeight();

      // Only apply constraints if there's actually a space problem
      if (availableHeight > 0 && availableHeight < 250) {
        // Set minimum and maximum constraints
        const minHeight = 120; // Minimum usable height
        const maxHeight = Math.max(minHeight, availableHeight);

        // Apply the calculated height
        dropdownMenu.style.maxHeight = `${maxHeight}px`;
        dropdownMenu.style.overflowY = 'auto';
      }

      // Apply width matching for user-dropdown components
      const toggleRect = customToggle.getBoundingClientRect();
      const exactWidth = toggleRect.width;

      dropdownMenu.style.width = `${exactWidth}px`;
      dropdownMenu.style.minWidth = `${exactWidth}px`;
      dropdownMenu.style.maxWidth = `${exactWidth}px`;
    }

    // For icon button dropdowns (sort, filter), don't apply any constraints
    // Let them use their natural positioning and sizing

    // Reset any fixed positioning
    dropdownMenu.style.position = '';
    dropdownMenu.style.top = '';
    dropdownMenu.style.left = '';
  }

  private calculateAvailableHeight(): number {
    if (!this.dropDown?.nativeElement) return 0;

    const position = this.getPosition(this.dropDown.nativeElement);
    const dropdownRect = this.dropDown.nativeElement.getBoundingClientRect();

    // Determine the container boundary (viewport or parent container)
    let containerBottom = window.innerHeight;

    // If there's an offsetParent (container), use its bottom boundary
    if (position.offsetParent) {
      const containerRect = position.offsetParent.getBoundingClientRect();
      containerBottom = containerRect.bottom;
    }

    // Calculate available space from dropdown bottom to container bottom
    const availableSpace = containerBottom - dropdownRect.bottom - 20; // 20px buffer

    return Math.max(0, availableSpace);
  }

  private getPosition(el: any) {
    let width = el?.clientWidth || 0;
    let height = el?.clientHeight || 0;
    let x = 0;
    let y = 0;
    while (
      el &&
      !el.classList.contains('modal') &&
      !el.classList.contains('modal-content') &&
      !isNaN(el.offsetLeft) &&
      !isNaN(el.offsetTop)
    ) {
      x += el.offsetLeft - el.scrollLeft;
      y += el.offsetTop - el.scrollTop;
      el = el.offsetParent;
    }
    return { top: y, left: x, width, height, offsetParent: el };
  }
}
